import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "@/App";
import { createAssignment } from "@/api/assignment";
import { postTask, updateTask } from "@/api/tasks";
import { Alert } from "@/components/_Shared/Form/Buttons/Alert";
import { useFormData } from "@/components/_Shared/Form/Hooks/useFormDataFetch";
import { MethodHandler } from "@/components/_Shared/Form/Utils/MethodHandler";
import { Error_Component } from "@/components/misc";
import { TaskProps } from "@/interfaces/tasks_types";
import { TaskFormBase } from "./TaskFormBase";
import { CustomResolver } from "./Utils/CustomResolver";
import { DefaultFormProps } from "./Utils/DefaultFormProps";
import { ImportFormProps } from "./Utils/ImportFormProps";

// Create a form data type that includes all possible fields
export type FormDataType = {
	task_id?: string;
	title: string;
	description?: string;
	completion_percentage: number;
	due_date: string;
	assigned_by?: { value: string; label: string } | null;
	assigned_to?: { value: string; label: string } | null;
	status: { value: "Active" | "Closed"; label: string } | null;
	category?: { value: string; label: string } | null;
	project?: { value: string; label: string } | null;
	priority: boolean;
};

export function TaskForm({
	task_data,
	method,
	is_assignment = false,
}: {
	task_data?: TaskProps;
	method: "POST" | "PUT";
	is_assignment?: boolean;
}) {
	const { user } = useAuth();

	const [assignment, setAssignment] = useState(is_assignment);

	const { projectManagers, projects, categories, fetchAllDataProject } =
		useFormData();

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting, isSubmitSuccessful },
		control,
		setError,
		reset,
	} = useForm<FormDataType>({
		defaultValues: task_data
			? ImportFormProps(task_data)
			: DefaultFormProps(user?.email ?? "", user?.name ?? ""),
		resolver: CustomResolver(assignment),
	});

	const onSubmit = async (data: FormDataType) => {
		if (assignment) {
			const assignmentData = {
				title: data.title,
				description: data.description ?? "",
				assigned_by: data.assigned_by?.value ?? "",
				assigned_to: data.assigned_to?.value ?? "",
				category: data.category?.value as
					| "Other"
					| "Electrical"
					| "Mechanical"
					| "Plumbing"
					| "Fire Protection",
				status: data.status?.value ?? "Active",
				completion_percentage: data.completion_percentage ?? 0,
				due_date: data.due_date ?? "",
				task_id: data.task_id ?? "",
			};

			const response = await createAssignment(assignmentData);

			if (response.operation != "COMPLETED") {
				setError("root", { message: response.message });
			}

			return;
		}

		// Check that project exists when not in assignment mode
		if (!data.project) {
			setError("project", { message: "Project is required for tasks" });
			return;
		}

		const formatted_data: TaskProps = {
			project: data.project.value ?? "",
			project_id: data.project.value ?? "",
			assigned_by: data.assigned_by?.value ?? "",
			assigned_to: data.assigned_to?.value ?? "",
			category:
				(data.category?.value as
					| "Other"
					| "Electrical"
					| "Mechanical"
					| "Plumbing"
					| "Fire Protection") ?? "Other",
			status: data.status?.value ?? "Active",
			completion_percentage: data.completion_percentage ?? 0,
			due_date: data.due_date ?? "",
			description: data.description ?? "",
			title: data.title ?? "",
			task_id: data.task_id ?? "",
			priority: data.priority ? "Urgent" : "Normal",
		};

		const method_handler = MethodHandler(method, postTask, updateTask);

		if (method_handler) {
			const response = await method_handler(formatted_data);

			if (response.operation != "COMPLETED") {
				setError("root", { message: response.message });
			}
		}
	};

	useEffect(() => {
		fetchAllDataProject();
	}, [fetchAllDataProject]);

	// Update form values when projectManagers are loaded and we have task data
	useEffect(() => {
		if (task_data && projectManagers && projectManagers.length > 0) {
			const formData = ImportFormProps(task_data);
			const matchedProject = projects?.find((p) => p.value === task_data.project_id);
			if (matchedProject) formData.project = matchedProject;
			reset(formData);
		}
	}, [projectManagers, projects, task_data, reset]);

	return (
		<>
			{errors.root && (
				<Error_Component
					errorString={errors.root.message ?? "An error occurred"}
				/>
			)}

			<TaskFormBase
				project_list={projects}
				manager_list={projectManagers}
				category_list={categories}
				currentTaskData={task_data || ({} as TaskProps)}
				method={method}
				isAssignment={assignment}
				setAssignment={setAssignment}
				register={register}
				errors={errors}
				control={control}
				isSubmitting={isSubmitting}
				onSubmit={handleSubmit(onSubmit)}
			/>

			{isSubmitSuccessful && (
				<Alert
					title={`Task ${method == "POST" ? "created" : "updated"} successfully`}
					description={`The task has been ${method == "POST" ? "created" : "updated"} successfully`}
					redirects={[
						{
							label: "Tasks",
							value: "/tasks",
						},
						{
							label: "Dashboard",
							value: "/main_menu",
						},
					]}
				/>
			)}
		</>
	);
}
